import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  Autocomplete,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  addGroupe,
  getGroupes,
  updateGroupe,
  deleteGroupes,
} from "../../../containers/groupes/groupesSlice";
import { CustomColorPicker } from "../../../components/inputs/CustomColorPicker";
import { checkPermission, showErrorToast } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";
import Langs from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": {
    fontSize: "16px",
    transition: TRANSITION_SMOOTH,
  },
  "& input::placeholder": {
    opacity: 0.45,
  },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileGroupeModal({ show, onHide }) {
  const dispatch = useDispatch();

  const settings = useSelector((state) => state.settings);
  const groupeModalType = useSelector((state) => state.common.groupeModalType);
  const selectedGroupeData = useSelector((state) => state.common.selectedGroupeData);
  const teams = useSelector((state) => state.teams.allTeams);
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const groupesList = useSelector((state) => state.groupes.list);

  const [formError, setFormError] = useState(null);
  const [data, setData] = useState({
    name: "",
    color: "",
    id_team: "",
    linked_groups: "",
  });

  useEffect(() => {
    if (selectedGroupeData) {
      setData(selectedGroupeData);
    } else {
      setData({ name: "", color: "", id_team: "", linked_groups: "" });
    }
  }, [selectedGroupeData]);

  function hide() {
    onHide();
    setFormError(null);
  }

  function getTitle() {
    if ((groupeModalType === "EDIT_GROUPE" || groupeModalType === "CONFIRM_DELETE_GROUPE") && data) {
      return data.label;
    } else if (groupeModalType) {
      return <Langs str={groupeModalType} />;
    }
    return "";
  }

  function handleAddGroupe() {
    if (!data || !data.id_team) {
      setFormError("MISSING_TEAM_ID");
    } else if (!data || !data.label || !data.label.length) {
      setFormError("MISSING_GROUPE_NAME");
    } else if (data.label && data.id_team) {
      setFormError(null);
      if (groupesList.length < 16) {
        dispatch(addGroupe({
          name: data.label,
          id_team: data.id_team,
          color: data.color,
          linked_groups: data.linked_groups,
        })).then(() => {
          dispatch(getGroupes(selectedTeamsIds));
        });
        hide();
      } else {
        showErrorToast(<Langs str="LIMIT_REACHED" />);
      }
    }
  }

  function handleDeleteGroupe() {
    setFormError(null);
    dispatch(deleteGroupes({ ids: [data.id] })).then(() => {
      dispatch(getGroupes(selectedTeamsIds));
    });
    hide();
  }

  function handleSaveGroupe() {
    if (!data || !data.label.length) {
      setFormError("MISSING_GROUPE_NAME");
    } else {
      setFormError(null);
      dispatch(updateGroupe({
        id: data.id,
        name: data.label,
        color: data.color,
        id_team: data.id_team,
        linked_groups: data.linked_groups,
      })).then(() => {
        dispatch(getGroupes(selectedTeamsIds));
      });
      hide();
    }
  }

  const handleGroupeNameChange = (event) => {
    setData({ ...data, label: event.target.value });
  };

  const handleTeamChange = (_, newValue) => {
    if (newValue) {
      setData({ ...data, id_team: newValue.value, linked_groups: "" });
    } else {
      setData({ ...data, id_team: "", linked_groups: "" });
    }
  };

  const handleLinkedGroupsChange = (_, selectedList) => {
    if (data) {
      const linkedGroups = selectedList.map((item) => item.value).join(",");
      setData({ ...data, linked_groups: linkedGroups });
    }
  };

  const handleGroupeColorChange = (hexColor) => {
    if (data) {
      setData({ ...data, color: hexColor });
    }
  };

  const teamOptions = teams && selectedTeamsIds
    ? teams.filter((t) =>
        selectedTeamsIds.includes(t.value) &&
        checkPermission(config.scopes.groups, config.permissionTypes.edit, t.value)
      )
    : [];

  const selectedTeamOption = teamOptions.find((t) => t.value === data.id_team) || null;

  const linkedGroupOptions = groupesList
    ? groupesList
        .filter((g) => g.id !== data.id && g.id_team === data.id_team)
        .map((g) => ({ value: g.id, label: g.label }))
    : [];

  const selectedLinkedGroups = data.linked_groups
    ? data.linked_groups
        .split(",")
        .filter(Boolean)
        .map((id) => {
          const found = linkedGroupOptions.find((o) => String(o.value) === String(id));
          return found || { value: id, label: id };
        })
    : [];

  let actionLabel = "ADD";
  let actionHandler = handleAddGroupe;
  if (groupeModalType === "EDIT_GROUPE") {
    actionLabel = "SAVE";
    actionHandler = handleSaveGroupe;
  } else if (groupeModalType === "CONFIRM_DELETE_GROUPE") {
    actionLabel = "DELETE";
    actionHandler = handleDeleteGroupe;
  }

  const isDeleteMode = groupeModalType === "CONFIRM_DELETE_GROUPE";
  const isEditMode = groupeModalType === "EDIT_GROUPE";

  return (
    <Dialog
      fullScreen
      open={!!show}
      onClose={hide}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "var(--surface-ground, #f4f6f8)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* AppBar */}
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={hide}
            aria-label="close"
            sx={{
              transition: TRANSITION_SMOOTH,
              borderRadius: "12px",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.06)", transform: "scale(1.08)" },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{ ml: 1.5, flex: 1, fontWeight: 600, fontSize: "1.15rem", letterSpacing: "-0.02em" }}
            variant="h6"
          >
            {getTitle()}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Body */}
      <Box
        sx={{
          p: 2.5,
          pb: 3,
          overflowY: "auto",
          flex: 1,
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
        }}
      >
        {formError && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: "12px" }}>
            <Langs str={formError} />
          </Alert>
        )}

        {isDeleteMode ? (
          <Box
            sx={{
              background: "rgba(255,255,255,0.65)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.45)",
              boxShadow: "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
              p: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="body1" sx={{ fontSize: "1.05rem", fontWeight: 500 }}>
              <Langs str="ARE_YOU_SURE" />
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              background: "rgba(255,255,255,0.65)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.45)",
              boxShadow: "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
              p: 2.5,
              transition: TRANSITION_SMOOTH,
            }}
          >
            {/* Team selector — only in ADD mode */}
            {!isEditMode && (
              <>
                <Typography variant="body2" sx={fieldLabelSx}>
                  <Langs str="TEAM" /> *
                </Typography>
                <Autocomplete
                  options={teamOptions}
                  getOptionLabel={(option) => option.label || ""}
                  value={selectedTeamOption}
                  onChange={handleTeamChange}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Sélectionnez une équipe"
                      error={formError === "MISSING_TEAM_ID"}
                      helperText={formError === "MISSING_TEAM_ID" ? <Langs str="MISSING_TEAM_ID" /> : ""}
                      sx={glassmorphicTextFieldSx}
                    />
                  )}
                  slotProps={{ popper: { sx: { zIndex: 1500 } } }}
                />
              </>
            )}

            {/* Group name */}
            <Typography variant="body2" sx={fieldLabelSx}>
              <Langs str="GROUPE_NAME" /> *
            </Typography>
            <TextField
              fullWidth
              value={data.label || ""}
              onChange={handleGroupeNameChange}
              inputProps={{ maxLength: 15 }}
              placeholder="Nom du groupe"
              error={formError === "MISSING_GROUPE_NAME"}
              helperText={formError === "MISSING_GROUPE_NAME" ? <Langs str="MISSING_GROUPE_NAME" /> : ""}
              sx={glassmorphicTextFieldSx}
            />

            {/* Linked groups */}
            <Typography variant="body2" sx={fieldLabelSx}>
              <Langs str="LINKED_GROUPES" />
            </Typography>
            <Autocomplete
              multiple
              options={linkedGroupOptions}
              getOptionLabel={(option) => option.label || ""}
              value={selectedLinkedGroups}
              onChange={handleLinkedGroupsChange}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value?.value)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Groupes liés" sx={glassmorphicTextFieldSx} />
              )}
              slotProps={{ popper: { sx: { zIndex: 1500 } } }}
            />

            {/* Color picker */}
            <Typography variant="body2" sx={fieldLabelSx}>
              <Langs str="GROUPE_COLOR" />
            </Typography>
            <Box sx={{ mt: 1 }}>
              <CustomColorPicker
                onChange={handleGroupeColorChange}
                color={data.color}
                colors={settings.groupesColors}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Sticky bottom buttons */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          p: 2.5,
          pb: "calc(1.25rem + env(safe-area-inset-bottom, 0px))",
          bgcolor: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
          borderTop: "1px solid rgba(255,255,255,0.5)",
          display: "flex",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          fullWidth
          onClick={hide}
          sx={{
            minHeight: 52,
            borderRadius: BORDER_RADIUS_BUTTON,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            transition: TRANSITION_SMOOTH,
            "&:hover": { transform: "scale(1.02)" },
            "&:active": { transform: "scale(0.98)" },
          }}
        >
          <Langs str="CANCEL" />
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={actionHandler}
          sx={{
            minHeight: 52,
            borderRadius: BORDER_RADIUS_BUTTON,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            backgroundColor: isDeleteMode ? "#d32f2f" : "#FF9015",
            boxShadow: isDeleteMode
              ? "0 4px 14px rgba(211,47,47,0.28)"
              : "0 4px 14px rgba(255,144,21,0.28)",
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              backgroundColor: isDeleteMode ? "#b71c1c" : "#e67e00",
              transform: "scale(1.02)",
              boxShadow: isDeleteMode
                ? "0 6px 20px rgba(211,47,47,0.35)"
                : "0 6px 20px rgba(255,144,21,0.35)",
            },
            "&:active": { transform: "scale(0.98)" },
          }}
        >
          <Langs str={actionLabel} />
        </Button>
      </Box>
    </Dialog>
  );
}
